import { ChatBotUIProduct } from "@jugl-web/domain-resources/chat-bot/types";
import { useTranslations } from "@jugl-web/utils";
import { FC, useEffect, useState } from "react";
import { priceFormatter } from "../../utils/priceFormatter";
import { ChatBotDrawer } from "../ChatBotDrawer";
import { ChatButton } from "../ChatButton";
import { ChatHeaderButton } from "../ChatHeaderButton";
import { ChatHeaderContainer } from "../ChatHeaderContainer";
import { ProductCard } from "../ProductCard";
import { ProductQuantitySelect } from "../ProductQuantitySelectButton";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";

interface ChatBotProductDetailsDrawerProps {
  isOpen: boolean;
  product: ChatBotUIProduct | null;
  onAddToCart: (product: ChatBotUIProduct, quantity: number) => void;
  onClose: () => void;
}

export const ChatBotProductDetailsDrawer: FC<
  ChatBotProductDetailsDrawerProps
> = ({ isOpen, product, onAddToCart, onClose }) => {
  const [quantity, setQuantity] = useState(1);

  const { t } = useTranslations();

  const totalPrice = product ? product.price * quantity : 0;

  const handleAddToCart = () => {
    if (!product) {
      return;
    }

    onAddToCart(product, quantity);
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      setQuantity(1);
    }
  }, [isOpen]);

  return (
    <ChatBotDrawer isOpen={isOpen} onClose={onClose}>
      {product && (
        <div className="flex h-full flex-col">
          {/* Header */}
          <ChatHeaderContainer>
            <div className="flex items-center gap-2">
              <ChatHeaderButton onClick={onClose}>
                <ArrowIcon />
              </ChatHeaderButton>
              <span className="font-secondary text-dark truncate text-[15px] font-medium">
                {t({
                  id: "chat-bot-product-details-drawer.item-details",
                  defaultMessage: "Item details",
                })}
              </span>
            </div>
          </ChatHeaderContainer>
          {/* Scrollable content */}
          <div className="jugl__custom-scrollbar flex-1 shrink-0 overflow-y-auto py-4 px-3">
            <ProductCard
              name={product.name}
              imageUrl={product.imageUrl}
              price={product.price}
            />
            <div className="mt-6 flex flex-col gap-4">
              <ProductQuantitySelect
                quantity={quantity}
                onQuantityChange={setQuantity}
              />
              {/* TODO: Put remaining options here */}
            </div>
          </div>
          {/* Footer */}
          <div className="flex shrink-0 flex-col gap-4 border-t border-l-0 border-r-0 border-b-0 border-solid border-[#F2F2F2] px-3 pt-4 pb-6">
            <div className="flex shrink-0 items-center justify-between gap-2 px-3">
              <span className="font-secondary text-dark text-sm font-normal leading-[150%]">
                {t({
                  id: "chat-bot-product-details-drawer.total",
                  defaultMessage: "Total",
                }).concat(":")}
              </span>
              <span className="font-secondary text-dark text-base font-semibold leading-[150%]">
                {priceFormatter.format(totalPrice)}
              </span>
            </div>
            <ChatButton
              variant="primary"
              fullWidth
              className="shrink-0"
              onClick={handleAddToCart}
            >
              {t({
                id: "chat-bot-product-details-drawer.add-to-cart",
                defaultMessage: "Add to cart",
              })}
            </ChatButton>
          </div>
        </div>
      )}
    </ChatBotDrawer>
  );
};
